import { useCallback, useEffect, useState } from 'react';
import {
  portalPricingOverrideService,
  type PricingOverrideRow,
} from '../../services/portalPricingOverrides';
import { formatDate } from '../../utils/dateFormatters';
import styles from './PortalPricingExemptionsPage.module.css';

/** Human ship-to label: prefer the Sage ship-to code, else city/state. */
function shipToLabel(row: PricingOverrideRow): string {
  const cityState = [row.ship_to_city, row.ship_to_state]
    .filter(Boolean)
    .join(', ');
  if (row.ship_to_code) {
    return cityState
      ? `Ship-to ${row.ship_to_code} — ${cityState}`
      : `Ship-to ${row.ship_to_code}`;
  }
  return cityState || 'Any location';
}

export default function PortalPricingExemptionsPage() {
  const [overrides, setOverrides] = useState<PricingOverrideRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await portalPricingOverrideService.listOverrides(showRetired);
      setOverrides(res.overrides);
    } catch (err: unknown) {
      setError('Failed to load pricing exemptions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [showRetired]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeCount = overrides.filter((o) => o.is_active).length;

  const filtered = overrides.filter((o) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return [
      o.customer_no,
      o.customer_name,
      o.pricing_customer_no,
      o.pricing_customer_name,
      o.ship_to_code,
      o.ship_to_city,
      o.ship_to_state,
      o.reason,
      o.authorized_by,
      o.requested_by,
    ].some((field) => field?.toLowerCase().includes(q));
  });

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <span>Loading pricing exemptions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <span>{error}</span>
        <button className={styles.errorRetry} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="pricing-exemptions-page">
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Pricing Exemptions</h1>
            <p className={styles.subtitle}>
              Standing agreements where a customer&apos;s orders price from
              another customer&apos;s private-label list. These apply
              automatically — orders matching a row below seed and re-price
              from the &ldquo;Prices From&rdquo; customer&apos;s special
              pricing and agreement discount.
            </p>
          </div>
        </div>
        <div className={styles.statsRow}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{activeCount}</span>
            <span className={styles.statLabel}>Active</span>
          </div>
          {showRetired && (
            <div className={styles.stat}>
              <span className={styles.statValue}>
                {overrides.length - activeCount}
              </span>
              <span className={styles.statLabel}>Retired</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.toolbar}>
        <label className={styles.retiredToggle}>
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(e) => setShowRetired(e.target.checked)}
          />
          Show retired exemptions
        </label>
        <div className={styles.searchWrapper}>
          <svg
            className={styles.searchIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search customers, locations, reasons..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Ship-To Location</th>
              <th>Prices From</th>
              <th>Reason</th>
              <th>Authorized / Requested By</th>
              <th>Status</th>
              <th>Since</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyRow}>
                  {searchQuery
                    ? 'No exemptions match your search'
                    : 'No standing pricing exemptions'}
                </td>
              </tr>
            ) : (
              filtered.map((o) => (
                <tr
                  key={o.id}
                  className={`${styles.row} ${o.is_active ? '' : styles.rowRetired}`}
                  data-testid="pricing-exemption-row"
                >
                  <td>
                    <span className={styles.codeBadge}>{o.customer_no}</span>
                    {o.customer_name && (
                      <span className={styles.custName}>{o.customer_name}</span>
                    )}
                  </td>
                  <td className={styles.tdShipTo}>{shipToLabel(o)}</td>
                  <td>
                    <span className={`${styles.codeBadge} ${styles.codeBadgePricing}`}>
                      {o.pricing_customer_no}
                    </span>
                    {o.pricing_customer_name && (
                      <span className={styles.custName}>
                        {o.pricing_customer_name}
                      </span>
                    )}
                  </td>
                  <td className={styles.tdReason}>{o.reason}</td>
                  <td className={styles.tdPeople}>
                    {o.authorized_by && <div>Auth: {o.authorized_by}</div>}
                    {o.requested_by && <div>Req: {o.requested_by}</div>}
                    {!o.authorized_by && !o.requested_by && <div>—</div>}
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        o.is_active ? styles.statusActive : styles.statusRetired
                      }`}
                    >
                      {o.is_active ? 'Active' : 'Retired'}
                    </span>
                  </td>
                  <td className={styles.tdSince}>{formatDate(o.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
